import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Box, CircularProgress, Modal, Paper, Typography } from "@mui/material";
import { useRootStore } from "../../../store/contexts";

type SavingOverlayProps = {
  /** Abierta mientras el formulario guarda (envío y relectura): el store lo dice con isSaving. */
  open: boolean;
  /** Cuánto esperar antes de mostrar «Guardando…»: un guardado corto no parpadea. */
  delayMs?: number;
};

/**
 * La capa que bloquea un formulario mientras guarda. Es un Modal sin fondo: cubre
 * la página, toma el foco y lo retiene, y se queda con el teclado, también con
 * Escape, que la barra escucha en el documento para cerrar el formulario. Así
 * nada de lo que se escriba mientras se guarda se pierde con la relectura: no se
 * puede escribir. Si el guardado tarda más que el umbral muestra «Guardando…» con
 * un indicador de progreso; si tarda menos, no se ve nada.
 */
export const SavingOverlay: React.FC<SavingOverlayProps> = observer(({ open, delayMs = 300 }) => {
  const { langStore } = useRootStore();
  const [showIndicator, setShowIndicator] = useState(false);
  useEffect(() => {
    if (!open) {
      setShowIndicator(false);
      return undefined;
    }
    const timer = setTimeout(() => setShowIndicator(true), delayMs);
    return () => clearTimeout(timer);
  }, [open, delayMs]);
  const label = langStore.savingInProgress;
  return (
    <Modal
      open={open}
      hideBackdrop
      disableEscapeKeyDown
      disableScrollLock
      // Ninguna tecla sale de la capa: ni al formulario ni a quien escuche en el documento.
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Box
        role="status"
        aria-label={label}
        aria-busy={open}
        tabIndex={-1}
        sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", outline: "none" }}
      >
        {showIndicator && (
          <Paper elevation={4} sx={{ display: "flex", alignItems: "center", gap: 2, px: 3, py: 2 }}>
            <CircularProgress size={24} />
            <Typography>{label}</Typography>
          </Paper>
        )}
      </Box>
    </Modal>
  );
});
